import React, { useEffect, useRef, useState } from "react"
import {
  Box, CloseButton, Flex, FocusLock, Heading, HStack, Icon as ChakraIcon, IconButton, List,
  ListItem, Switch, Text,
} from "@chakra-ui/react"
import { useIntercom } from "react-use-intercom"
import Button from "components/Buttons/Button"
import Icon from "components/elements/Icon"
import Modal from "components/dialogs/Modal"
import { BLOCK_LABELS, BlockId } from "../layout"

export const PANEL_WIDTH = "360px"

type Props = {
  ids: BlockId[]
  hidden: BlockId[]
  onToggle: (id: BlockId) => void
  onMove: (id: BlockId, target: BlockId) => void
  onHighlight: (id: BlockId | null) => void
  onReset: () => void
  onCancel: () => void
  onDone: () => void
  hasChanges: boolean
}

const MOVE_BUTTON = {
  size: "xs", variant: "ghost", color: "gray.500", _hover: { color: "gray.700", bg: "gray.100" },
} as const

const GripIcon = () => (
  <ChakraIcon viewBox="0 0 8 14" w="6px" h="10px" color="gray.500" flexShrink={0} aria-hidden>
    {[1, 7, 13].flatMap((y) => [1, 7].map((x) => (
      <circle key={`${x}:${y}`} cx={x} cy={y} r={1.25} fill="currentColor" />
    )))}
  </ChakraIcon>
)

const ToTopIcon = () => (
  <Box as="span" display="inline-flex" flexDirection="column" alignItems="center" gap="1px">
    <Box w={2.5} h="1.5px" bg="currentColor" borderRadius="full" />
    <Icon icon="up-arrow" size={3} />
  </Box>
)

const CustomizePanel = ({
  ids, hidden, onToggle, onMove, onHighlight, onReset, onCancel, onDone,
  hasChanges,
}: Props) => {
  const [dragged, setDragged] = useState<BlockId | null>(null)
  // dragenter also fires for every element inside a row; moving again for those
  // would swap the two rows back and forth.
  const [over, setOver] = useState<BlockId | null>(null)
  const [hovered, setHovered] = useState<BlockId | null>(null)
  const [focused, setFocused] = useState<BlockId | null>(null)
  const heading = useRef<HTMLHeadingElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  // Open while asking to save or discard after a click outside.
  const [asked, setAsked] = useState(false)
  const { update: updateIntercom } = useIntercom()

  useEffect(() => onHighlight(hovered ?? focused), [hovered, focused, onHighlight])
  // The chat launcher sits over the panel's Done button.
  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: true })

    return () => updateIntercom({ hideDefaultLauncher: false })
  }, [updateIntercom])

  // Unsaved changes ask to save or discard first; with none, the panel just closes.
  const hasChangesRef = useRef(hasChanges)
  hasChangesRef.current = hasChanges
  const leave = useRef(() => {})
  leave.current = () => (hasChanges ? setAsked(true) : onCancel())

  // The page behind is inert, so without this a click there just does nothing.
  useEffect(() => {
    if (asked) return undefined

    const clickOutside = (event: MouseEvent) => {
      if (panel.current?.contains(event.target as Node)) return
      if (hasChangesRef.current) {
        event.preventDefault()
        event.stopPropagation()
      }
      leave.current()
    }
    document.addEventListener("click", clickOutside, true)

    return () => document.removeEventListener("click", clickOutside, true)
  }, [asked])

  const dragOnto = (target: BlockId) => {
    if (!dragged || target === over) return

    setOver(target)
    onMove(dragged, target)
  }
  const endDrag = () => {
    setDragged(null)
    setOver(null)
  }

  return (
    <FocusLock initialFocusRef={heading} isDisabled={asked}>
      <Flex
        ref={panel}
        as="aside"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customize-dashboard-title"
        direction="column"
        position="fixed"
        top={0}
        right={0}
        bottom={0}
        w={PANEL_WIDTH}
        maxW="100vw"
        zIndex="modal"
        bg="white"
        borderLeftWidth={1}
        borderColor="gray.200"
        boxShadow="lg"
        onKeyDown={(e) => e.key === "Escape" && !asked && leave.current()}
      >
        <Box px={5} pt={5} pb={4} borderBottomWidth={1} borderColor="gray.200">
          <Flex justify="space-between" align="center" gap={2}>
            <Heading
              id="customize-dashboard-title"
              ref={heading}
              tabIndex={-1}
              fontSize="md"
              fontWeight="semibold"
              _focus={{ outline: "none" }}
            >
              Customize dashboard
            </Heading>
            <CloseButton size="sm" onClick={onCancel} />
          </Flex>
          <Text mt={1} color="gray.600">
            Reorder and show or hide blocks. Applies to this event for everyone on the team.
          </Text>
        </Box>
        <List flex={1} overflowY="auto" px={2} py={3} spacing={0.5}>
          {ids.map((id, index) => {
            const label = BLOCK_LABELS[id]
            const atTop = index === 0

            return (
              <ListItem
                key={id}
                data-block={id}
                draggable
                // Pressing a row moves focus to it, inside the focus lock; focus leaving the lock
                // is pulled back, which cancels the drag.
                tabIndex={-1}
                _focusVisible={{ outline: "none" }}
                display="flex"
                alignItems="center"
                gap={2}
                h={10}
                px={3}
                borderRadius="md"
                cursor="grab"
                opacity={dragged === id ? 0.4 : 1}
                _hover={{ bg: "gray.50" }}
                _focusWithin={{ bg: "gray.50" }}
                sx={{ "&:hover .row-actions, &:focus-within .row-actions": { opacity: 1 } }}
                onMouseEnter={() => setHovered(id)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setFocused(id)}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(null)
                }}
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", id)
                  setDragged(id)
                  setOver(id)
                }}
                onDragEnter={() => dragOnto(id)}
                onDragOver={(e) => dragged && e.preventDefault()}
                onDragEnd={endDrag}
                onDrop={(e) => {
                  e.preventDefault()
                  endDrag()
                }}
              >
                <GripIcon />
                <Text flex={1} minW={0} noOfLines={1} fontWeight="semibold">{label}</Text>
                <HStack className="row-actions" spacing={0} opacity={0} transition="opacity 0.1s">
                  <IconButton
                    aria-label={`Move ${label} to top`}
                    icon={<ToTopIcon />}
                    {...MOVE_BUTTON}
                    isDisabled={atTop}
                    onClick={() => onMove(id, ids[0])}
                  />
                  <IconButton
                    aria-label={`Move ${label} up`}
                    icon={<Icon icon="up-arrow" size={3} />}
                    {...MOVE_BUTTON}
                    isDisabled={atTop}
                    onClick={() => onMove(id, ids[index - 1])}
                  />
                  <IconButton
                    aria-label={`Move ${label} down`}
                    icon={<Icon icon="down-arrow" size={3} />}
                    {...MOVE_BUTTON}
                    isDisabled={index === ids.length - 1}
                    onClick={() => onMove(id, ids[index + 1])}
                  />
                </HStack>
                <Switch
                  aria-label={`Show ${label}`}
                  isChecked={!hidden.includes(id)}
                  onChange={() => onToggle(id)}
                />
              </ListItem>
            )
          })}
        </List>
        <Flex px={5} py={4} gap={2} borderTopWidth={1} borderColor="gray.200" align="center">
          <Button size="sm" variant="ghost" onClick={onReset}>Reset to default</Button>
          <Box flex={1} />
          <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={onDone}>Done</Button>
        </Flex>
        <Modal
          size="md"
          show={asked}
          hide={() => setAsked(false)}
          title="Save layout changes?"
          actions={[
            <Button key="keep" variant="ghost" onClick={() => setAsked(false)}>
              Keep customizing
            </Button>,
            <Button key="discard" variant="outline" colorScheme="button.danger" onClick={onCancel}>
              Discard
            </Button>,
            <Button key="save" onClick={onDone}>Save</Button>,
          ]}
        >
          <Text>
            You changed the dashboard layout. Save it for everyone on the team, or discard the
            changes?
          </Text>
        </Modal>
      </Flex>
    </FocusLock>
  )
}

export default CustomizePanel
